/*
 * Copyright 2020 Google Inc. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License.
 *  You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software
 *  distributed under the License is distributed on an "AS IS" BASIS,
 *  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *  See the License for the specific language governing permissions and
 *  limitations under the License.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Message from './message/Message';
import { toHex32 } from './Helpers';
import SyncFrame from './SyncFrame';
export default class Stream {
    constructor(client, service, localId, remoteId, options) {
        this.client = client;
        this.service = service;
        this.localId = localId;
        this.remoteId = remoteId;
        this.options = options;
        this.awaitMessage = null;
    }
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.write('CLSE');
            if (this.options.debug) {
                console.log(`Closed stream ${this.service}`);
                console.log(` local_id: 0x${toHex32(this.localId)}`);
                console.log(` remote_id: 0x${toHex32(this.remoteId)}`);
            }
            this.client.unregisterStream(this);
        });
    }
    consumeMessage(msg) {
        if (msg.header.arg0 === 0 || msg.header.arg0 !== this.remoteId ||
            msg.header.arg1 === 0 || msg.header.arg1 !== this.localId) {
            return false;
        }
        if (this.awaitMessage) {
            this.awaitMessage(msg);
            this.awaitMessage = null;
        }
        return true;
    }
    write(cmd, data) {
        return __awaiter(this, void 0, void 0, function* () {
            const message = this.newMessage(cmd, data);
            yield this.client.sendMessage(message);
        });
    }
    read() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise(resolve => {
                this.awaitMessage = resolve;
            });
        });
    }
    /**
     *
     * Retrieves a file from device to a local file. The remote path is the path to
     * the file that will be returned. Just as for the SEND sync request the file
     * received is split up into chunks. The sync response id is "DATA" and length is
     * the chunk size. After follows chunk size number of bytes. This is repeated
     * until the file is transferred. Each chunk will not be larger than 64k.
     * When the file is transferred a sync response "DONE" is retrieved where the
     * length can be ignored.
     *
     * @param {string} remotePath path to the file to be pulled from the device
     * @returns {Promise<Blob>} a Blog with the file contents.
     */
    pull(remotePath) {
        return __awaiter(this, void 0, void 0, function* () {
            const encoder = new TextEncoder();
            const encodedFilename = encoder.encode(remotePath);
            // Sends RECV with filename length.
            const recvFrame = new SyncFrame('RECV', encodedFilename.byteLength);
            const wrteRecvMessage = this.newMessage('WRTE', recvFrame.toDataView());
            yield this.client.sendMessage(wrteRecvMessage);
            const wrteRecvResponse = yield this.read();
            if (wrteRecvResponse.header.cmd !== 'OKAY') {
                throw new Error('WRTE/RECV failed: ' + wrteRecvResponse);
            }
            // 17. We send the path of the file we want again sdcard/someFile.txt
            const wrteFilenameMessage = this.newMessage('WRTE', new DataView(encodedFilename.buffer));
            yield this.client.sendMessage(wrteFilenameMessage);
            // 18. Device sends us OKAY
            const wrteFilenameResponse = yield this.read();
            if (wrteFilenameResponse.header.cmd !== 'OKAY') {
                throw new Error('WRTE/filename failed: ' + wrteFilenameResponse);
            }
            const okayMessage = this.newMessage('OKAY');
            let fileDataMessage = yield this.read();
            yield this.client.sendMessage(okayMessage);
            let syncFrame = SyncFrame.fromDataView(new DataView(fileDataMessage.data.buffer.slice(0, 8)));
            let buffer = new Uint8Array(fileDataMessage.data.buffer.slice(8));
            const chunks = [];
            while (syncFrame.cmd !== 'DONE') {
                while (syncFrame.byteLength >= buffer.byteLength) {
                    fileDataMessage = yield this.read();
                    yield this.client.sendMessage(okayMessage);
                    // Join both arrays
                    const newLength = buffer.byteLength + fileDataMessage.data.byteLength;
                    const newBuffer = new Uint8Array(newLength);
                    newBuffer.set(buffer, 0);
                    newBuffer.set(new Uint8Array(fileDataMessage.data.buffer), buffer.byteLength);
                    buffer = newBuffer;
                }
                chunks.push(buffer.slice(0, syncFrame.byteLength).buffer);
                buffer = buffer.slice(syncFrame.byteLength);
                syncFrame = SyncFrame.fromDataView(new DataView(buffer.slice(0, 8).buffer));
                buffer = buffer.slice(8);
            }
            return new Blob(chunks);
        });
    }
    newMessage(cmd, data) {
        return Message.newMessage(cmd, this.localId, this.remoteId, this.options.useChecksum, data);
    }
    static open(device, service, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const localId = Stream.nextId++;
            let remoteId = 0;
            const m = Message.open(localId, remoteId, service, options.useChecksum);
            yield device.sendMessage(m);
            let response;
            do {
                response = yield device.awaitMessage();
            } while (response.header.arg1 !== localId);
            if (response.header.cmd !== 'OKAY') {
                throw new Error('OPEN Failed');
            }
            remoteId = response.header.arg0;
            if (options.debug) {
                console.log(`Opened stream ${service}`);
                console.log(` local_id: 0x${toHex32(localId)}`);
                console.log(` remote_id: 0x${toHex32(remoteId)}`);
            }
            const stream = new Stream(device, service, localId, remoteId, options);
            device.registerStream(stream);
            return stream;
        });
    }
}
Stream.nextId = 1;
//# sourceMappingURL=Stream.js.map